import { create } from 'zustand'
import { Business, CreateBusinessData } from '../models/business'
import { businessService } from '../services'
import { UserRole } from '../enums'

interface BusinessFilters {
  status?: 'all' | 'active' | 'inactive'
}

interface BusinessState {
  businesses: Business[]
  currentBusiness: Business | null
  isLoading: boolean
  error: string | null

  // Actions
  fetchBusinesses: (filters?: BusinessFilters) => Promise<void>
  fetchBusinessById: (id: number) => Promise<void>
  fetchBusinessesByRole: (userRole: UserRole, businessId?: number, filters?: BusinessFilters) => Promise<void>
  createBusiness: (data: CreateBusinessData) => Promise<Business | null>
  updateBusiness: (id: number, data: Partial<Business>) => Promise<Business | null>
  deleteBusiness: (id: number) => Promise<boolean>
  uploadLogo: (id: number, file: File) => Promise<boolean>
  
  setCurrentBusiness: (business: Business | null) => void
  
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  clearError: () => void
}

// Track if a request is in progress to prevent duplicate requests
let isFetchingBusinesses = false

export const useBusinessStore = create<BusinessState>((set) => ({
  businesses: [],
  currentBusiness: null,
  isLoading: false,
  error: null,

  fetchBusinesses: async (filters?: BusinessFilters) => {
    // Prevent duplicate requests
    if (isFetchingBusinesses) return
    
    isFetchingBusinesses = true
    try {
      set({ isLoading: true, error: null })
      const businesses = await businessService.getBusinesses(filters)
      set({ businesses })
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to fetch businesses'
      set({ error: errorMessage })
    } finally {
      set({ isLoading: false })
      isFetchingBusinesses = false
    }
  },

  fetchBusinessById: async (id: number) => {
    try {
      set({ isLoading: true, error: null })
      const business = await businessService.getBusinessById(id)
      set({ businesses: [business], currentBusiness: business })
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to fetch business'
      set({ error: errorMessage })
    } finally {
      set({ isLoading: false })
    }
  },

  fetchBusinessesByRole: async (userRole: UserRole, businessId?: number, filters?: BusinessFilters) => {
    // Prevent duplicate requests
    if (isFetchingBusinesses) return
    
    isFetchingBusinesses = true
    try {
      set({ isLoading: true, error: null })

      // If user is BUSINESS_OWNER, fetch all businesses
      if (userRole === UserRole.BUSINESS_OWNER) {
        const businesses = await businessService.getBusinesses(filters)
        set({ businesses })
      } 
      // If user is EMPLOYEE or CASHIER, fetch only their business
      else if (userRole === UserRole.BUSINESS_EMPLOYEE || userRole === UserRole.BUSINESS_CASHIER) {
        if (!businessId) {
          throw new Error('Business ID is required for EMPLOYEE and CASHIER roles')
        }
        const business = await businessService.getBusinessById(businessId)
        set({ businesses: [business], currentBusiness: business })
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to fetch businesses'
      set({ error: errorMessage })
    } finally {
      set({ isLoading: false })
      isFetchingBusinesses = false
    }
  },

  createBusiness: async (data: CreateBusinessData) => {
    try {
      set({ isLoading: true, error: null })
      const newBusiness = await businessService.createBusiness(data)
      set(state => ({ 
        businesses: [...state.businesses, newBusiness]
      }))
      return newBusiness
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to create business'
      set({ error: errorMessage })
      return null
    } finally {
      set({ isLoading: false })
    }
  },

  updateBusiness: async (id: number, data: Partial<Business>) => {
    try {
      set({ isLoading: true, error: null })
      const updatedBusiness = await businessService.updateBusiness(id.toString(), data)
      set(state => ({
        businesses: state.businesses.map(b => 
          b.id === id ? updatedBusiness : b
        ),
        currentBusiness: state.currentBusiness?.id === id ? updatedBusiness : state.currentBusiness
      }))
      return updatedBusiness
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to update business'
      set({ error: errorMessage })
      return null
    } finally {
      set({ isLoading: false })
    }
  },

  deleteBusiness: async (id: number) => {
    try {
      set({ isLoading: true, error: null })
      await businessService.deleteBusiness(id.toString())
      
      set(state => ({
        businesses: state.businesses.filter(b => b.id !== id),
        currentBusiness: state.currentBusiness?.id === id ? null : state.currentBusiness
      }))
      return true
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || error.message || 'Failed to delete business'
      set({ error: errorMessage })
      return false
    } finally {
      set({ isLoading: false })
    }
  },

  uploadLogo: async (id: number, file: File) => {
    try {
      set({ isLoading: true, error: null })
      const logoUrl = await businessService.uploadLogo(id.toString(), file)
      
      // Update business with new logo URL
      set(state => ({
        businesses: state.businesses.map(b => 
          b.id === id ? { ...b, logoUrl } : b
        ),
        currentBusiness: state.currentBusiness?.id === id 
          ? { ...state.currentBusiness, logoUrl }
          : state.currentBusiness
      }))
      
      return true
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to upload logo'
      set({ error: errorMessage })
      return false
    } finally {
      set({ isLoading: false })
    }
  },

  setCurrentBusiness: (business: Business | null) => {
    set({ currentBusiness: business })
  },

  setLoading: (loading: boolean) => set({ isLoading: loading }),
  setError: (error: string | null) => set({ error }),
  clearError: () => set({ error: null })
}))